import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { useMyOrdersQuery } from '../../redux/api/bookingApi';
import { useJsApiLoader } from '@react-google-maps/api';
import { setDropoff, setPickup, setRouteMeta } from '../../redux/features/rideFlowSlice';
import { placeFromGoogle } from './rideConfig';
import LocationInput from './LocationInput';
import RideMap from './RideMap';
import { useRideShell } from './RideApp';
import '../../css/ride.css';

const SAVES_KEY = 'mbzSavedPlaces';

const readSaves = () => {
  try {
    const raw = localStorage.getItem(SAVES_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return { Home: parsed.Home || null, Work: parsed.Work || null };
  } catch {
    return { Home: null, Work: null };
  }
};

const hasCoords = (p) => p?.label && Number.isFinite(p.lat) && Number.isFinite(p.lng);

export default function RideHome() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openMenu } = useRideShell();
  const { user, isAuthenticated } = useSelector((s) => s.auth);
  const { pickup, dropoff, routeMeta } = useSelector((s) => s.rideFlow);

  // Loads the Maps JS API (+ Places lib) for this screen's inputs and geocoding.
  useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  const { data: ordersData, isLoading: ordersLoading } = useMyOrdersQuery(undefined, {
    skip: !isAuthenticated,
  });

  const [saves, setSaves] = useState(readSaves);
  const [pickupText, setPickupText] = useState('');
  const [dropoffText, setDropoffText] = useState('');
  const [busy, setBusy] = useState(false);
  const geocoderRef = useRef(null);

  // Rehydrate input texts from a persisted (sessionStorage) ride flow.
  useEffect(() => {
    if (pickup?.label) setPickupText(pickup.label);
    if (dropoff?.label) setDropoffText(dropoff.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getGeocoder = () => {
    if (!geocoderRef.current && window.google?.maps) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
    return geocoderRef.current;
  };

  const geocodeAddress = (address) =>
    new Promise((resolve) => {
      const geocoder = getGeocoder();
      if (!geocoder) return resolve(null);
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results?.length) {
          resolve({
            label: results[0].formatted_address || address,
            short: address.split(',')[0],
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
            city:
              (results[0].address_components || []).find((c) => (c.types || []).includes('locality'))
                ?.long_name || 'Karachi',
          });
        } else {
          resolve(null);
        }
      });
    });

  // Most-recent unique destinations (newest first).
  const recents = useMemo(() => {
    const orders = ordersData?.orders || [];
    const seen = new Set();
    const list = [];
    for (const o of orders) {
      const dest = o?.googleMap?.destination;
      if (dest && !seen.has(dest)) {
        seen.add(dest);
        list.push({ address: dest, short: dest.split(',')[0] });
      }
      if (list.length >= 5) break;
    }
    return list;
  }, [ordersData]);

  const onPickupSelect = (place) => {
    const p = placeFromGoogle(place);
    dispatch(setPickup(p));
    setPickupText(p.label);
  };

  const onDropoffSelect = (place) => {
    const p = placeFromGoogle(place);
    dispatch(setDropoff(p));
    setDropoffText(p.label);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error('Geolocation is not supported');
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        dispatch(setPickup({
          label: 'Current location',
          short: 'Current location',
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          city: 'Karachi',
        }));
        setPickupText('Current location');
        setBusy(false);
      },
      () => {
        setBusy(false);
        toast.error('Could not read your location');
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  };

  // Saved Home/Work chips act as destination shortcuts, and as
  // "save current destination" buttons when empty.
  const onSavedChip = (name) => {
    if (saves[name]) {
      dispatch(setDropoff(saves[name]));
      setDropoffText(saves[name].label);
    } else if (dropoff?.label) {
      const next = { ...saves, [name]: dropoff };
      setSaves(next);
      localStorage.setItem(SAVES_KEY, JSON.stringify(next));
      toast.success(`${name} saved`);
    } else {
      toast('Pick a destination first, then tap again to save it');
    }
  };

  const onRecentTap = (address) => {
    setDropoffText(address);
    // Store address without coords; Find resolves it via geocoder.
    dispatch(setDropoff({ label: address, short: address.split(',')[0], lat: undefined, lng: undefined, city: 'Karachi' }));
  };

  const fetchRoute = (from, to) =>
    new Promise((resolve) => {
      const ds = new window.google.maps.DirectionsService();
      ds.route({ origin: from, destination: to, travelMode: window.google.maps.TravelMode.DRIVING }, (res, status) => {
        if (status === window.google.maps.DirectionsStatus.OK && res?.routes?.[0]) {
          const leg = res.routes[0].legs[0];
          resolve({ distance: leg.distance.text, duration: leg.duration.text });
        } else {
          resolve(null);
        }
      });
    });

  const onFindRide = async () => {
    if (!window.google?.maps) return toast.error('Map is still loading — try again in a second');
    if (!pickup?.label && !pickupText) return toast.error('Enter a pickup location');
    if (!dropoff?.label && !dropoffText) return toast.error('Where are you going?');

    setBusy(true);
    try {
      // Resolve any typed-but-not-selected addresses through the geocoder.
      let from = hasCoords(pickup) ? pickup : await geocodeAddress(pickup?.label || pickupText);
      let to = hasCoords(dropoff) ? dropoff : await geocodeAddress(dropoff?.label || dropoffText);

      if (!from) { toast.error('Could not find the pickup address'); return; }
      if (!to) { toast.error('Could not find the destination'); return; }

      if (from !== pickup) dispatch(setPickup(from));
      if (to !== dropoff) dispatch(setDropoff(to));

      const meta = routeMeta && from?.label === pickup?.label && to?.label === dropoff?.label
        ? routeMeta
        : await fetchRoute(from.label, to.label);

      if (!meta) { toast.error('No route found between these places'); return; }

      dispatch(setRouteMeta(meta));
      navigate('/ride/select');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="ride-topbar">
        <button className="ride-icon-btn" onClick={openMenu} aria-label="Menu">
          <i className="bi bi-list" />
        </button>
        <Link to="/ride" className="ride-brand-pill">
          <img src="/images/MBZ-logobg.png" alt="" />
          <span>MBZ Rides</span>
        </Link>
        <Link to="/ride/account" className="ride-icon-btn" aria-label="Account">
          {user?.avatar?.url
            ? <img src={user.avatar.url} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }} />
            : <i className="bi bi-person" />}
        </Link>
      </div>

      <div className="ride-map-area">
        <RideMap pickup={pickup} dropoff={dropoff} />
      </div>

      <div className="ride-panel">
        <div className="ride-grabber" />
        <div className="ride-panel-inner">
          <div className="ride-title">Where to?</div>
          <div className="ride-subtitle mb-3">Rides & loads across the city — cash on delivery</div>

          <div className="ride-search-stack">
            <LocationInput
              id="ride-pickup"
              placeholder="Pickup location"
              value={pickupText}
              onChange={setPickupText}
              onSelect={onPickupSelect}
              icon="bi-circle-fill"
            />
            <LocationInput
              id="ride-dropoff"
              placeholder="Where are you going?"
              value={dropoffText}
              onChange={setDropoffText}
              onSelect={onDropoffSelect}
              icon="bi-square-fill"
            />
            <div
              className="d-flex align-items-center gap-2 px-3 py-2"
              style={{ borderTop: '1px solid var(--color-border-light)', background: 'var(--color-bg)' }}
            >
              <button className="btn btn-sm p-0 text-primary" style={{ textDecoration: 'none', fontSize: '0.82rem' }} onClick={useMyLocation} disabled={busy}>
                <i className="bi bi-crosshair" /> Use your current location
              </button>
            </div>
          </div>

          <div className="ride-section-label">Saved for you</div>
          <div className="ride-chips mb-2">
            {['Home', 'Work'].map((name) => (
              <button key={name} className={`ride-chip ${saves[name] ? 'selected' : ''}`} onClick={() => onSavedChip(name)}>
                <i className={`bi ${name === 'Home' ? 'bi-house-door' : 'bi-briefcase'}`} />
                {saves[name] ? name : `Save destination as ${name}`}
              </button>
            ))}
          </div>

          {isAuthenticated && (
            <>
              <div className="ride-section-label">Recent</div>
              {ordersLoading ? (
                <div className="small text-muted py-2">Loading your recent trips…</div>
              ) : recents.length ? (
                recents.map((r) => (
                  <button key={r.address} className="ride-rowlink" onClick={() => onRecentTap(r.address)}>
                    <span className="ride-row-ico"><i className="bi bi-clock-history" /></span>
                    <span className="ride-row-main">
                      <div className="ride-row-title">{r.short}</div>
                      <div className="ride-row-sub">{r.address}</div>
                    </span>
                    <i className="bi bi-chevron-right chev" />
                  </button>
                ))
              ) : (
                <div className="small text-muted py-2">Your recent destinations will show up here.</div>
              )}
            </>
          )}

          <div className="mt-4">
            <button className="ride-cta" onClick={onFindRide} disabled={busy}>
              {busy ? (
                <span className="ride-spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
              ) : (
                <>
                  <i className="bi bi-search" /> Find a ride
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
